import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { forwardRef, type HTMLAttributes, type ComponentRef, type ComponentPropsWithoutRef } from 'react'

import { cn } from '@/lib/utils'

// Re-export Radix primitives
export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal
export const DialogClose = DialogPrimitive.Close

// Overlay component
export const DialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-50', className)}
      {...props}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Content component
export const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Classes for the Overlay rendered behind the content */
    overlayClassName?: string
    /** Classes for the built-in Close button */
    closeClassName?: string
  }
>(({ className, overlayClassName, closeClassName, children, ...props }, ref) => {
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn('fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%]', className)}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none',
            closeClassName
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

// Header component
export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={cn('flex flex-col', className)} {...props} />
}
DialogHeader.displayName = 'DialogHeader'

// Footer component
export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end', className)} {...props} />
}
DialogFooter.displayName = 'DialogFooter'

// Title component
export const DialogTitle = forwardRef<
  ComponentRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={className}
      {...props}
    />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

// Description component
export const DialogDescription = forwardRef<
  ComponentRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={className}
      {...props}
    />
  )
})
DialogDescription.displayName = DialogPrimitive.Description.displayName
